import React, { ChangeEvent } from "react";
import { Grid2 as Grid, Stack, Button, IconButton, Typography, CircularProgress } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { useTranslate } from "@refinedev/core";

import { Modal, Form, BooksAutocomplete } from "@shared/components";
import { CreateAudioTracksModalProps } from "./types";
import { useCreateAudioTracksModal } from "./controller";

export const CreateAudioTracksModal = React.memo(({ modalRef, onComplete }: CreateAudioTracksModalProps) => {
  const translate = useTranslate();

  const {
    methods,
    fields,
    rowStatus,
    isPending,
    currentIndex,
    handleAddTrack,
    handleAddTracksFromFiles,
    handleRemoveTrack,
    handleReset,
    submitTracks,
  } = useCreateAudioTracksModal(modalRef, onComplete);

  const handleFilesSelected = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      handleAddTracksFromFiles(e.target.files);
    }
    e.target.value = "";
  };

  return (
    <Modal
      ref={modalRef}
      title={translate("v1/audiobook_admin.titles.create")}
      confirmLabel={translate("v1/audiobook_admin.buttons.save_all")}
      onConfirm={submitTracks}
      onOpen={handleReset}
      onClose={handleReset}
      methods={methods}
      isPending={isPending}
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <BooksAutocomplete
            name="book"
            label={translate("v1/audiobook_admin.fields.book")}
            labelField="title"
            queryHookFilters={{ type: "audiobook" }}
            required
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Button
            component="label"
            variant="outlined"
            startIcon={<UploadFileIcon />}
            disabled={isPending}
          >
            {translate("v1/audiobook_admin.buttons.select_files")}
            <input
              type="file"
              accept="audio/*"
              multiple
              hidden
              onChange={handleFilesSelected}
            />
          </Button>
        </Grid>

        {isPending && currentIndex !== null && (
          <Grid size={{ xs: 12 }}>
            <Typography color="primary" style={{ fontSize: 14 }}>
              {translate("v1/audiobook_admin.messages.sending", {
                current: currentIndex + 1,
                total: fields.length,
              })}
            </Typography>
          </Grid>
        )}

        {fields.map((field, index) => {
          const status = rowStatus[field.id]?.status ?? "idle";
          const error = rowStatus[field.id]?.error;
          const disabled = isPending || status === "success";

          return (
            <Grid key={field.id} size={{ xs: 12 }}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <Grid size={{ xs: 3 }}>
                  <Form.Input
                    name={`tracks.${index}.position`}
                    label={translate("v1/audiobook_admin.fields.position")}
                    disabled={disabled}
                  />
                </Grid>

                <Grid size="grow">
                  <Form.AudioFilePicker
                    name={`tracks.${index}.file`}
                    label={translate("v1/audiobook_admin.fields.file")}
                    disabled={disabled}
                  />
                  {error && (
                    <Typography style={{ fontSize: 12, color: "#d32f2f" }}>
                      {error}
                    </Typography>
                  )}
                </Grid>

                <Stack direction="row" alignItems="center" sx={{ height: 45 }}>
                  {status === "sending" && <CircularProgress size={20} />}
                  {status === "success" && <CheckCircleIcon color="success" />}
                  {status === "error" && <ErrorIcon color="error" />}

                  {status !== "success" && (
                    <IconButton
                      size="small"
                      disabled={isPending || fields.length === 1}
                      onClick={() => handleRemoveTrack(index)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
              </Stack>
            </Grid>
          );
        })}

        <Grid size={{ xs: 12 }}>
          <Button
            startIcon={<AddIcon />}
            onClick={handleAddTrack}
            disabled={isPending}
          >
            {translate("v1/audiobook_admin.buttons.add_track")}
          </Button>
        </Grid>
      </Grid>
    </Modal>
  );
});
